/**
 * The company list, searched in the browser (owner, 2026-10-13).
 *
 * `/data/companies.json` is a static file on Firebase Hosting (Google's CDN,
 * never cold), regenerated by scripts/export_company_list.py before a frontend
 * deploy. It is fetched once per page load and searched locally, so the search
 * box answers on every keystroke even when the backend is cold or down. The
 * ranking mirrors tools.equity_holdings.search_stocks exactly: ISIN exact →
 * ticker exact → ticker prefix → substring in ticker or name, then by shorter
 * name. A query with no local match falls back to the server (searchStocks).
 */
export interface CompanyRow { ticker: string; company_name: string; isin?: string | null }

const URL = "/data/companies.json";
let rowsPromise: Promise<CompanyRow[] | null> | null = null;

/** Load the list once; null when unavailable (missing file, offline, bad JSON). */
export function loadCompanies(fetchImpl: typeof fetch = fetch): Promise<CompanyRow[] | null> {
  rowsPromise ??= fetchImpl(URL, { credentials: "omit" })
    .then((r) => (r.ok ? r.json() : null))
    .then((d: { rows?: [string, string, string][] } | null) =>
      Array.isArray(d?.rows) && d!.rows.length > 0
        ? d!.rows.map(([ticker, company_name, isin]) => ({ ticker, company_name, isin: isin || null }))
        : null)
    .catch(() => null)
    .then((rows) => { if (!rows) rowsPromise = null; return rows; });   // retry next time on failure
  return rowsPromise;
}

/** Same ranking as the server. Fewer than two characters returns nothing. */
export function searchCompanies(rows: CompanyRow[], q: string, limit = 20): CompanyRow[] {
  const qu = (q || "").trim().toUpperCase();
  if (qu.length < 2) return [];
  const scored: [number, number, CompanyRow][] = [];
  for (const r of rows) {
    const t = r.ticker.toUpperCase();
    const name = (r.company_name || "").toUpperCase();
    const isin = (r.isin || "").toUpperCase();
    let rank: number;
    if (isin && isin === qu) rank = 0;
    else if (t === qu) rank = 1;
    else if (t.startsWith(qu)) rank = 2;
    else if (t.includes(qu) || name.includes(qu)) rank = 3;
    else continue;
    scored.push([rank, (r.company_name || "").length, r]);
  }
  scored.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  return scored.slice(0, Math.min(Math.max(limit, 1), 50)).map((x) => x[2]);
}

/** Tests only. */
export function __resetCompanyIndex(): void { rowsPromise = null; }
